import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Gift, DollarSign, Users, ArrowRight, Sparkles, Zap } from 'lucide-react';
import { MonkeyLogo } from '../components/MonkeyLogo';
import { useAuth } from '../App';

const AffiliatePage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-subtle" data-testid="affiliate-page">
      <nav className="bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <MonkeyLogo size={32} />
              <span className="text-xl font-bold">LinkMonkey</span>
            </Link>
            <div className="flex gap-2">
              {user ? (
                <Link to="/dashboard"><Button variant="ghost" size="sm">Dashboard</Button></Link>
              ) : (
                <>
                  <Link to="/login"><Button variant="ghost" size="sm">Login</Button></Link>
                  <Link to="/signup"><Button size="sm">Sign Up Free</Button></Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-16">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10 mb-4">
            <Gift className="w-8 h-8 text-emerald-500" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Earn from every referral</h1>
          <p className="text-lg text-muted-foreground max-w-lg mx-auto">
            Every LinkMonkey signup is automatically an affiliate. Share your link, earn recurring commission on every Pro upgrade.
          </p>
        </div>

        {/* Logged-in CTA — already in the program */}
        {user ? (
          <Card className="p-6 mb-8 border-2 border-emerald-500/40 bg-emerald-500/[0.05]" data-testid="already-affiliate-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">You're already in 🎉</h2>
                <p className="text-sm text-muted-foreground">Your referral link is live and ready to share.</p>
              </div>
            </div>
            <div className="rounded-lg bg-background border border-border px-4 py-3 mb-4">
              <p className="text-xs text-muted-foreground mb-1">Your link</p>
              <code className="text-sm font-mono break-all">linkmonkey.io/signup?ref={user.username}</code>
            </div>
            <Link to="/affiliate/dashboard">
              <Button className="w-full" size="lg" data-testid="goto-affiliate-dashboard-btn">
                View your earnings dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Card>
        ) : (
          <Card className="p-6 mb-8 border-2 border-primary/30 bg-primary/[0.04]" data-testid="signup-to-earn-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold mb-1">Free signup. Instant affiliate.</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Create a free LinkMonkey account and you get a personal referral link the moment you sign up — no application, no approval.
                </p>
                <Link to="/signup">
                  <Button size="lg" data-testid="signup-affiliate-cta">
                    Get my referral link <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        )}

        {/* Earnings breakdown */}
        <Card className="p-6 mb-8 border-2 border-emerald-500/20 bg-emerald-500/[0.02]" data-testid="earnings-breakdown">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            <h2 className="text-lg font-semibold">How you earn</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div className="rounded-xl bg-card border border-border p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Monthly Pro referral</p>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-2xl font-semibold tracking-tight">€1</span>
                <span className="text-sm text-muted-foreground">/mo per active signup</span>
              </div>
              <p className="text-xs text-muted-foreground">25% of every €4/mo subscription, recurring as long as they stay.</p>
            </div>
            <div className="rounded-xl bg-card border border-border p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Yearly Pro referral</p>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-2xl font-semibold tracking-tight">€10</span>
                <span className="text-sm text-muted-foreground">/yr per active signup</span>
              </div>
              <p className="text-xs text-muted-foreground">25% of every €40/yr subscription, paid every renewal.</p>
            </div>
          </div>

          <div className="rounded-xl bg-background/60 border border-border p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Example: 10 signups via your link</p>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">10 signups (5 stay free, 5 go Pro)</span>
                <span className="font-medium tabular-nums">Tracked as 10</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">2 on monthly Pro</span>
                <span className="font-medium tabular-nums text-emerald-600">+ €2 /mo recurring</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">3 on yearly Pro</span>
                <span className="font-medium tabular-nums text-emerald-600">+ €30 /yr recurring</span>
              </li>
              <li className="flex items-center justify-between pt-2 border-t border-border">
                <span className="font-semibold">Your year-1 earnings</span>
                <span className="font-semibold tabular-nums text-emerald-600">= €54</span>
              </li>
            </ul>
            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              Free signups don't generate commission but are still tracked. Only paid Pro subscribers pay out.
            </p>
          </div>
        </Card>

        {/* Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          <Card className="p-5 text-center">
            <DollarSign className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <p className="font-semibold">Recurring income</p>
            <p className="text-xs text-muted-foreground mt-1">Paid every time your referrals renew</p>
          </Card>
          <Card className="p-5 text-center">
            <Users className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="font-semibold">No limit</p>
            <p className="text-xs text-muted-foreground mt-1">Refer as many people as you want</p>
          </Card>
          <Card className="p-5 text-center">
            <Gift className="w-6 h-6 text-orange-500 mx-auto mb-2" />
            <p className="font-semibold">Monthly payouts</p>
            <p className="text-xs text-muted-foreground mt-1">PayPal or SEPA bank — your choice</p>
          </Card>
        </div>

        <div className="text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">&larr; Back to home</Link>
        </div>
      </div>
    </div>
  );
};

export default AffiliatePage;
